import { S25Util } from "../util/s25-util";
import { Proto } from "./Proto";
import { S25ItemI } from "./S25ItemI";
import NumericalBoolean = Proto.NumericalBoolean;
import { EventQuota } from "./EventQuota";
import { Item } from "./Item";

export namespace EventQuotaInspect {
    export interface InspectRequestDate {
        occId: number;
        startDt: Date;
        endDt: Date;
        rsrvId?: number;
        eventId?: number;
        eventStartDt?: Date;
        eventEndDt?: Date;
    }

    export interface InspectRequestLocation {
        roomId: number;
        occId: number;
    }

    export interface InspectRequest {
        targetUsername: string;
        organizationId?: number;
        eventTypeId?: number;
        locationAssignments: InspectRequestLocation[];
        dates: InspectRequestDate[];
    }

    export interface InspectQuotaOverride {
        accessLevel: string;
        quotaEnforcementActive: boolean;
        message: string;
    }

    export interface InspectQueryOccurrence {
        occId: number;
        startDt: string;
        endDt: string;
        objectId?: number;
        objectTypeId?: number;
    }

    export interface InspectCountedReservation {
        rsrvId: number;
        eventId?: number;
        startDt: string;
        endDt: string;
        durationMinutes: number;
        isCandidate: boolean;
        objectId?: number;
    }

    export interface InspectWindow {
        windowStart: Date;
        windowEnd: Date;
        objectId?: number;
        quotaMax?: number;
        rsrvCount?: number;
        durationMax?: number;
        durationTotal?: number;
        countedReservations: InspectCountedReservation[];
    }

    export interface InspectQuotaOccurrence {
        occId: number;
        allowed: boolean;
        allowedCount: boolean;
        allowedTime: boolean;
        blockingReason?: "count" | "time" | "both";
        windows: InspectWindow[];
    }

    export interface InspectQuotaResult {
        scope: "user" | "org";
        quotaId: number;
        quotaName: string;
        quotaType: EventQuota.TypeIds;
        active: boolean;
        quantity?: number;
        maxMinutes?: number;
        windowMode: EventQuota.WindowMode;
        duration?: number;
        durationType?: string;
        skipExisting: boolean;
        countByEvent: boolean;
        groupObjects: boolean;
        locations: S25ItemI[];
        occurrences: InspectQuotaOccurrence[];
    }

    export interface InspectResponse {
        targetUsername: string;
        targetContactId: number;
        quotaOverride: InspectQuotaOverride;
        queryOccurrences: InspectQueryOccurrence[];
        quotas: InspectQuotaResult[];
    }

    export interface InspectRawCountedReservation {
        rsrv_id?: number;
        event_id?: number;
        start_dt?: Proto.ISODateString;
        end_dt?: Proto.ISODateString;
        duration_minutes?: number;
        is_candidate?: NumericalBoolean;
        object_id?: number;
    }

    export interface InspectRawWindow {
        window_start?: Proto.ISODateString;
        window_end?: Proto.ISODateString;
        object_id?: number;
        quota_max?: number;
        rsrv_count?: number;
        duration_max?: number;
        duration_total?: number;
        counted_reservations?: InspectRawCountedReservation[];
    }

    export interface InspectRawQuotaOccurrence {
        occ_id?: number;
        allowed?: NumericalBoolean;
        allowed_count?: number;
        allowed_time?: number;
        blocking_reason?: "count" | "time" | "both";
        windows?: InspectRawWindow[];
    }

    export interface InspectRawQuotaLocation {
        item_id?: number;
        item_name?: string;
    }

    export interface InspectRawQuota {
        scope?: "user" | "org";
        quota_id?: number;
        quota_name?: string;
        quota_type?: number;
        active?: Proto.NumericalBoolean;
        quantity?: number;
        max_minutes?: number;
        window_mode?: string;
        duration?: number;
        duration_type?: string;
        skip_existing?: NumericalBoolean;
        count_by_event?: NumericalBoolean;
        group_objects?: NumericalBoolean;
        locations?: InspectRawQuotaLocation[];
        occurrences?: InspectRawQuotaOccurrence[];
    }

    export interface InspectRawQueryOccurrence {
        occ_id?: number;
        start_dt?: string;
        end_dt?: string;
        object_id?: number;
        object_type_id?: number;
    }

    export interface InspectRawQuotaOverride {
        access_level?: string;
        quota_enforcement_active?: boolean | number | string;
        message?: string;
    }

    export interface InspectRawResponseRoot {
        target_username?: string;
        target_contact_id?: number;
        quota_override?: InspectRawQuotaOverride;
        query_occurrences?: InspectRawQueryOccurrence[];
        quotas?: InspectRawQuota[];
    }

    export function inspectResponseFromRoot(root: InspectRawResponseRoot): InspectResponse {
        const quotaOverrideRaw = root?.quota_override ?? {};
        return {
            targetUsername: S25Util.toStr(root.target_username),
            targetContactId: S25Util.parseInt(root.target_contact_id),
            quotaOverride: {
                accessLevel: S25Util.toStr(quotaOverrideRaw?.access_level),
                quotaEnforcementActive: S25Util.toBool(quotaOverrideRaw?.quota_enforcement_active),
                message: S25Util.toStr(quotaOverrideRaw?.message),
            },
            queryOccurrences: S25Util.array
                .forceArray(root.query_occurrences)
                .map((queryRow: InspectRawQueryOccurrence) => ({
                    occId: S25Util.parseInt(queryRow.occ_id),
                    startDt: S25Util.toStr(queryRow.start_dt),
                    endDt: S25Util.toStr(queryRow.end_dt),
                    objectId: S25Util.parseInt(queryRow.object_id) || undefined,
                    objectTypeId: S25Util.parseInt(queryRow.object_type_id) || undefined,
                })),
            quotas: S25Util.array.forceArray(root.quotas).map((quotaRow) => inspectQuotaFromRaw(quotaRow)),
        };
    }

    function inspectQuotaFromRaw(quotaRow: InspectRawQuota): InspectQuotaResult {
        return {
            scope: quotaRow.scope === "org" ? "org" : "user",
            quotaId: S25Util.parseInt(quotaRow.quota_id),
            quotaName: S25Util.toStr(quotaRow.quota_name),
            quotaType: S25Util.parseInt(quotaRow.quota_type),
            active: S25Util.toBool(quotaRow.active),
            quantity: S25Util.parseInt(quotaRow.quantity) || undefined,
            maxMinutes: S25Util.parseInt(quotaRow.max_minutes) || undefined,
            windowMode: EventQuota.normalizeWindowMode(quotaRow.window_mode),
            duration: S25Util.parseInt(quotaRow.duration) || undefined,
            durationType: S25Util.toStr(quotaRow.duration_type) || undefined,
            skipExisting: S25Util.toBool(quotaRow.skip_existing),
            countByEvent: S25Util.toBool(quotaRow.count_by_event),
            groupObjects: S25Util.toBool(quotaRow.group_objects),
            locations: S25Util.array.forceArray(quotaRow.locations).map((locationRow) => ({
                itemId: S25Util.parseInt(locationRow.item_id),
                itemName: S25Util.toStr(locationRow.item_name),
                itemTypeId: Item.Ids.Location,
            })),
            occurrences: S25Util.array
                .forceArray(quotaRow.occurrences)
                .map((occRow) => inspectOccurrenceFromRaw(occRow)),
        };
    }

    function inspectOccurrenceFromRaw(occRow: InspectRawQuotaOccurrence): InspectQuotaOccurrence {
        const blockingReason = occRow.blocking_reason;
        return {
            occId: S25Util.parseInt(occRow.occ_id),
            allowed: S25Util.toBool(occRow.allowed),
            allowedCount: S25Util.toBool(occRow.allowed_count),
            allowedTime: S25Util.toBool(occRow.allowed_time),
            blockingReason: blockingReason,
            windows: occRow.windows?.map((windowRow) => inspectWindowFromRaw(windowRow)) || [],
        };
    }

    function inspectWindowFromRaw(windowRow: InspectRawWindow): InspectWindow {
        return {
            windowStart: S25Util.date.parseDropTZ(windowRow.window_start),
            windowEnd: S25Util.date.parseDropTZ(windowRow.window_end),
            objectId: S25Util.parseInt(windowRow.object_id) || undefined,
            quotaMax: S25Util.parseInt(windowRow.quota_max) || undefined,
            rsrvCount: S25Util.parseInt(windowRow.rsrv_count) || undefined,
            durationMax: S25Util.parseInt(windowRow.duration_max) || undefined,
            durationTotal: S25Util.parseInt(windowRow.duration_total) || undefined,
            countedReservations:
                windowRow.counted_reservations?.map((countedRow) => ({
                    rsrvId: S25Util.parseInt(countedRow.rsrv_id),
                    eventId: S25Util.parseInt(countedRow.event_id) || undefined,
                    startDt: S25Util.toStr(countedRow.start_dt),
                    endDt: S25Util.toStr(countedRow.end_dt),
                    durationMinutes: S25Util.parseInt(countedRow.duration_minutes),
                    isCandidate: S25Util.toBool(countedRow.is_candidate),
                    objectId: S25Util.parseInt(countedRow.object_id) || undefined,
                })) || [],
        };
    }
}
